/*
 * AnalyticsTracker — sends a GA4 page_view on every route change.
 * Required because the site is a single-page app (wouter): without this,
 * GA would only record the initial page load.
 */
import { useEffect } from "react";
import { useLocation } from "wouter";

const GA_MEASUREMENT_ID = "G-8PJDTZYHF6";
let initialized = false;
let loading = false;
let pendingPageView: { path: string; title: string } | null = null;

function sendPendingPageView() {
  if (!pendingPageView || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: pendingPageView.path,
    page_title: pendingPageView.title,
    page_location: window.location.origin + pendingPageView.path,
    send_to: GA_MEASUREMENT_ID,
  });
  pendingPageView = null;
}

function loadAnalytics() {
  if (initialized || loading || typeof document === "undefined") return;
  loading = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) { window.dataLayer?.push(args); };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.onload = () => {
    initialized = true;
    loading = false;
    sendPendingPageView();
  };
  script.onerror = () => { loading = false; };
  document.head.appendChild(script);
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[][];
  }
}

export default function AnalyticsTracker() {
  const [location] = useLocation();

  useEffect(() => {
    pendingPageView = { path: location, title: document.title };
    if (initialized) sendPendingPageView();
    else {
      const start = () => loadAnalytics();
      const timeout = window.setTimeout(start, 5000);
      window.addEventListener("pointerdown", start, { once: true, passive: true });
      window.addEventListener("keydown", start, { once: true, passive: true });
      window.addEventListener("scroll", start, { once: true, passive: true });
      return () => {
        window.clearTimeout(timeout);
        window.removeEventListener("pointerdown", start);
        window.removeEventListener("keydown", start);
        window.removeEventListener("scroll", start);
      };
    }
  }, [location]);

  return null;
}
